import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import axios from "axios";

import { convertToRichText } from "../getters/gettersV2";
import { useGenErrorMsgBanners } from "../useGenErrorMsgBanners";
import { UIContent } from "../../UIContent";
import { useAppSettingsContext } from "../../contexts/appSettingsContext";
import { useBannerContext } from "oolib";

const getTagsV3 = (type, options = {}) => { 
  let apiString, queryParams;

  if (options.tagCategory === "userTags") {
    apiString = "/api/users/getAllUsers";
    queryParams = {
      findQuery: JSON.stringify({
        profileType: type,
        ...(options.filters || {}),
      }),
      activeSort: options.activeSort ? JSON.stringify(options.activeSort) : undefined,
      postDALFilter: JSON.stringify(options.postDALFilter || {}),
    };

    return axios.get(apiString, { params: queryParams }).then((res) => {
      return res.data.data
    });
  } else { //contentTags
    apiString = "/api/discovery/getData";
    queryParams = {
      configs: JSON.stringify({
        contentTypes: [type],
        findQuery: {
          ...(options.filters || {}),
          kp_published_status: 'published'
        },
        activeLang: options.activeLang,
        ...(options.activeSort ? { activeSort: options.activeSort } : {}),
      }),
    };

    return axios.post(apiString, queryParams).then((res) => {
      return res.data.data
    });
  }
};

export const useGetTagsV3 = (type, options = {}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;
  const {genErrorMsgBanners} = useGenErrorMsgBanners()
  //multiple
  const query = useQuery(
    ["tagsV3", type, options, activeLang],
    () => getTagsV3(type, { ...options, activeLang }),
    {
      enabled: options.enabled,
      onError: (err) => {
        genErrorMsgBanners({err})			
			},
    }
  );
  return query;
};

export const useMultipleGetTagsV3 = (tagTypes = [], options = {}) => {
  //tagTypes can come in as string or object
  let tagTypesData = tagTypes.every((d) => typeof d === "string")
    ? tagTypes.map((d) => ({ tagType: d }))
    : tagTypes;
  const multipleQueries = tagTypesData.map((d) => {
    return {
      queryKey: d.tagType,
      queryFn: () => getTagsV3(d.tagType, { ...options, ...(d.options || {}) }),
      enabled: options.enabled,
    };
  });

  return useQueries(multipleQueries);
};

const updateThemesSubThemesTagsDataAndCountInCache = ({res, queryClient, platformConfigs}) => {
if (platformConfigs.SELF_MANAGED_BASE_CONFIGS?.enable === true) {

         
          //run a invalidate queries on the getData cache of tags,subThemes,themes
          queryClient.invalidateQueries({
            queryKey: ["contentListing", ["tags", "subThemes", "themes"]]
          })
          

          // Get the content type from the response
          const contentType = res.data?.meta?.kp_content_type;
          // Update the platformConfigs cache to increment the count
          queryClient.setQueryData('platformConfigs', (oldData) => {
            if (!oldData) return oldData;
            const updatedData = { ...oldData };
            // Make sure the nested structure exists
            if (updatedData.annotationTagsCount) {
              const currentCounts = { ...updatedData.annotationTagsCount };
              // Increment the appropriate count based on content type
              if (contentType === 'tags' && typeof currentCounts.tags === 'number') {
                currentCounts.tags += 1;
              } else if (contentType === 'subThemes' && typeof currentCounts.subThemes === 'number') {
                currentCounts.subThemes += 1;
              } else if (contentType === 'themes' && typeof currentCounts.themes === 'number') {
                currentCounts.themes += 1;
              }
              updatedData.annotationTagsCount = currentCounts;
            }
            // console.log({updatedData});
            return updatedData;
          });
        }
}

const updateContentListingCacheForThisTag = ({queryClient, contentType}) => {
 // Get all queries from the cache
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.getAll();
        
        // Find and invalidate contentListing queries that match the type
        queries.forEach(query => {
          const queryKey = query.queryKey;
          
          // Check if this is a contentListing query
          if (queryKey && queryKey[0] === 'contentListing') {
            // Check if queryKey[1] exists and contains the type
            if (queryKey[1] && Array.isArray(queryKey[1]) && queryKey[1].includes(contentType)) {
              console.log('time to invalidate...')
              queryClient.invalidateQueries(queryKey);
            }
          }
        });
}


export const useCreateTagV3 = () => {
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  const platformConfigs = queryClient.getQueryData('platformConfigs')
  return useMutation(
    ({ type, display, customFields = {} }) => {
      
        return axios.post(
          `/api/content/createAndPublishContent/${type}`,
          {
            main: { 
              ...(customFields.main || {}),
              title: display 
            },
            meta: { 
              ...(customFields.meta || {}),
              kp_content_type: type 
            },
            ...(customFields.tags 
              ? { tags: customFields.tags } 
              : {}
            )
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        ).then(res => ({ data: res.data.content }));
       
    },
    {
      onSuccess: (res, { type }) => {
        queryClient.invalidateQueries(["tagsV3", type]);
        
       
        updateContentListingCacheForThisTag({queryClient, contentType: type})

        /**
         * needed on SELF MANAGED because:
         * if the count of themes or subthemes goes to / increases from zero,
         * we hide/show some features        
         */
        updateThemesSubThemesTagsDataAndCountInCache({res, queryClient, platformConfigs})


      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};
